define('tracks/trackdata-processing',["require", "exports", "../geo/geo", "../util/calculus", "../util/convert", "../util/math", "../util/physics", "../util/polar-library", "../util/savitzky-golay", "../util/wse"], function (require, exports, geo_1, calculus_1, convert_1, math_1, physics_1, polar_library_1, savitzky_golay_1, wse_1) {
    "use strict";
    Object.defineProperty(exports, "__esModule", { value: true });
    exports.filterlengthfromsamplerate = exports.samplesamplerate = exports.lslice = exports.computeAcceleration = exports.processSustained = exports.processData = void 0;
    // const mass = 80 // kg
    // const planformArea = 2 // m^2
    /**
     * Compute stuff like local date, speed, distance and altitude relative to start point, etc
     */
    function processData(track) {
        var startPoint = track.startPoint();
        var lastPoint;
        for (var _i = 0, _a = track.data; _i < _a.length; _i++) {
            var point = _a[_i];
            if (point.millis) {
                point.date = track.utcToBrowser(point.millis);
            }
            // Speed
            point.groundspeed = Math.sqrt(point.vN * point.vN + point.vE * point.vE);
            // Glide
            var glide = -point.groundspeed / point.climb;
            if (point.groundspeed > 3.5 && point.climb < 0.5 && point.climb !== 0 && 0 <= glide && glide <= 29.9) {
                point.glideRatio = glide;
            }
            if (track.inTrim(point.millis)) {
                // Distance from start point
                if (point.lat !== undefined && point.lng !== undefined && point.alt !== undefined) {
                    if (lastPoint === undefined) {
                        point.x = 0;
                        point.y = 0;
                        point.distance = 0;
                    }
                    else {
                        var straightDistance = (0, geo_1.distance)(startPoint.lat, startPoint.lng, point.lat, point.lng);
                        point.x = convert_1.default.distance.localValue(straightDistance);
                        point.y = convert_1.default.distance.localValue(point.alt - startPoint.alt);
                        var lastDistance = convert_1.default.distance.localValue((0, geo_1.distance)(lastPoint.lat, lastPoint.lng, point.lat, point.lng));
                        point.distance = lastPoint.distance + lastDistance;
                    }
                    lastPoint = point;
                }
            }
        }
    }
    exports.processData = processData;
    /**
     * Compute sustained speed info
     */
    function processSustained(track) {
        var timerStart = new Date().getTime();
        if (track.data[0].smooth)
            return;
        var data = track.data;
        // Compute smoothed speeds
        //use gps sample rate
        var samplerate = samplesamplerate(data);
        var fl = filterlengthfromsamplerate(samplerate);
        // magical adjustible acceleration filter , fl should be odd
        var smoothed = true; // use smoothed speeds?
        //console.log("gps rate:", samplerate, "acceleration filter size:", fl)
        if (samplerate >= 25) {
            (0, savitzky_golay_1.smoothPointsN)(data, 25, 25, 25);
            //console.log("SG filter size: 25, 25, 25")
        }
        else if (samplerate >= 20) {
            (0, savitzky_golay_1.smoothPointsN)(data, 25, 21);
            //console.log("SG filter size: 25, 21")
        }
        else if (samplerate >= 10) {
            (0, savitzky_golay_1.smoothPointsN)(data, 25, 11);
            //console.log("SG filter size: 25, 11")
        }
        else {
            (0, savitzky_golay_1.smoothPoints)(data);
            //console.log("SG filter size: 15")
        }
        var _loop_1 = function (i) {
            var point = data[i];
            point.index = i;
            var vN = point.smooth.vN;
            var vE = point.smooth.vE;
            var vD = point.smooth.vD;
            // Compute lift and drag by slope fitting
            var localPoints = lslice(i, data, fl);
            //console.log(data[i].millis,localPoints)
            // Normalize time, because otherwise math explodes
            var startTime = localPoints[0].millis;
            var getT = function (d) { return (d.millis - startTime) * 1e-3; };
            var accelN = (0, calculus_1.getSlope)(localPoints, getT, function (d) { return smoothed ? d.smooth.vN : d.vN; });
            var accelE = (0, calculus_1.getSlope)(localPoints, getT, function (d) { return smoothed ? d.smooth.vE : d.vE; });
            var accelD = (0, calculus_1.getSlope)(localPoints, getT, function (d) { return smoothed ? d.smooth.vD : -d.climb; });
            var accelDminusG = accelD - physics_1.gravity;
            // Calculate acceleration due to drag
            var vel = Math.sqrt(vN * vN + vE * vE + vD * vD);
            var proj = (accelN * vN + accelE * vE + accelDminusG * vD) / vel;
            var dragN = proj * vN / vel;
            var dragE = proj * vE / vel;
            var dragD = proj * vD / vel;
            var accelDrag = Math.sqrt(dragN * dragN + dragE * dragE + dragD * dragD);
            // Calculate acceleration due to lift
            var liftN = accelN - dragN;
            var liftE = accelE - dragE;
            var liftD = accelDminusG - dragD;
            var accelLift = Math.sqrt(liftN * liftN + liftE * liftE + liftD * liftD);
            // TODO: Get true air pressure from barometer
            // temp is the biggest factor for density
            // the best way would be to use the internet to
            // lookup / estimate the local temp/pressure/humidity
            // based on local airport recorded observations
            // and adjusted to the current altitude...complicated.
            // standard atmosphere is 'good enough' but speeds will
            // change with large changes in temp. And to a lesser
            // extent with pressure and humidity.
            var airPressure = (0, physics_1.altitudeToPressure)(point.alt);
            var temperature = (0, physics_1.temp)(point.alt); //+ 20 // temp offset
            // From https://en.wikipedia.org/wiki/Density_of_air
            point.rho = airPressure / (physics_1.gasConst / physics_1.mmAir) / temperature;
            // From https://en.wikipedia.org/wiki/Dynamic_pressure
            var dynamicPressure = point.rho * vel * vel / 2;
            // calc correct sign. drag should be negative when in same direction as velocity (in aeroplane when climbing)
            var dragsign = -(0, math_1.signum)(dragN * vN + dragE * vE + dragD * vD);
            //console.log(dragsign)
            // Calculate lift and drag coefficients
            // get exit weight from user!!
            var m = 77.5;
            var s = 2;
            point.cl = m * accelLift / dynamicPressure / s;
            point.cd = m * accelDrag / dynamicPressure / s * dragsign;
            // Calculate magic wingsuit coefficients
            var kl = accelLift / physics_1.gravity / vel / vel;
            var kd = accelDrag / physics_1.gravity / vel / vel * dragsign;
            var divisor = Math.pow(kl * kl + kd * kd, 0.75);
            point.sustained_x = kl / divisor;
            point.sustained_y = kd / divisor;
            // Calculate roll angle
            var smoothGroundspeed = Math.sqrt(vN * vN + vE * vE);
            var rollMagnitude = Math.acos((1 - accelD / physics_1.gravity - kd * vel * vD) / (kl * smoothGroundspeed * vel));
            var rollSign = (0, math_1.signum)(liftN * -vE + liftE * vN);
            var roll = rollSign * rollMagnitude;
            if (!isNaN(roll)) {
                point.roll = roll;
            }
            // Calculate wingsuit aoa
            // aoa=PI*e^(PI/2*cl)
            // use default ws aoa
            //point.wsaoa = point.cl > 1.4 ? Math.PI * Math.exp(Math.PI / 2 * 1.4) * Math.PI / 180 : Math.PI * Math.exp(Math.PI / 2 * point.cl) * Math.PI / 180
            var vs = Math.sqrt(point.sustained_x * point.sustained_x + point.sustained_y * point.sustained_y);
            var trho = airPressure / (physics_1.gasConst / physics_1.mmAir) / (temperature + 20);
            var susi = (0, wse_1.getsustainedindex)(vs, polar_library_1.corvidpolar, trho).n;
            if (susi < 0) {
                var maxg = (0, wse_1.getmaxg)(polar_library_1.corvidpolar, trho);
                var mgvs = Math.sqrt(maxg.vxs * maxg.vxs + maxg.vys * maxg.vys);
                if (vs < mgvs) {
                    susi = 0;
                }
                else {
                    susi = 1;
                }
            }
            point.wsaoa = (0, polar_library_1.indexaoa)(susi, polar_library_1.genericwingsuitaoaindexes, polar_library_1.genericwingsuitaoas) * Math.PI / 180;
        };
        // console.log(`Smooth processing took ${new Date().getTime() - timerStart} ms`)
        //console.log(data)
        for (var i = 0; i < data.length; i++) {
            _loop_1(i);
        }
        var computeTime = new Date().getTime() - timerStart;
        if (computeTime > 100) {
            console.error("sustained speed processing took " + computeTime + " ms");
        }
        //console.log(track)
    }
    exports.processSustained = processSustained;
    /**
     * Compute sustained speed info
     */
    function computeAcceleration(track, fl) {
        var data = track.data;
        if (!track.data[0].smooth) {
            track.processSustained(); //change this to just do smoothing
        }
        var accel = [];
        // Compute smoothed speeds
        //smoothPoints(data)
        // console.log(`Smooth processing took ${new Date().getTime() - timerStart} ms`)
        if (!fl)
            fl = 5;
        var _loop_2 = function (i) {
            // Compute lift and drag by slope fitting
            var localPoints = lslice(i, data, fl);
            // Normalize time, because otherwise math explodes
            var startTime = localPoints[0].millis;
            var getT = function (d) { return (d.millis - startTime) * 1e-3; };
            var accelN = (0, calculus_1.getSlope)(localPoints, getT, function (d) { return d.smooth.vN; });
            var accelE = (0, calculus_1.getSlope)(localPoints, getT, function (d) { return d.smooth.vE; });
            var accelD = (0, calculus_1.getSlope)(localPoints, getT, function (d) { return d.smooth.vD; });
            accel[i] = { an: accelN, ae: accelE, ad: accelD };
        };
        for (var i = 0; i < data.length; i++) {
            _loop_2(i);
        }
        //console.log(accel)
        return accel;
    }
    exports.computeAcceleration = computeAcceleration;
    function saferoll(prev, next, alpha) {
        var roll = 0;
        var cond = Math.abs(next - prev) > 180;
        if (cond) {
            var unwraproll = next > 0 ? next - 360 : next + 360;
            roll = prev * (1 - alpha) + unwraproll * alpha;
            if (roll > 180) {
                roll = roll - 360;
            }
            if (roll <= 180) {
                roll = roll + 360;
            }
        }
        else {
            roll = prev * (1 - alpha) + next * alpha;
        }
        return roll;
    }
    function median(values) {
        if (values.length === 0)
            throw new Error("No inputs");
        values.sort(function (a, b) {
            return a - b;
        });
        var half = Math.floor(values.length / 2);
        return values[half];
    }
    function lslice(i, data, fl) {
        var half = Math.floor(fl / 2);
        var localPoints = data.slice(i - half, i + half + 1); // 5
        // fix begining and end
        // handle ends by reduceing slice size
        if (i < half) {
            localPoints = data.slice(0, Math.max(2 * (i + 1) - 1, 2));
        }
        if (i > data.length - half + 1) {
            localPoints = data.slice(Math.min(i - (data.length - i) + 1, data.length - 2), data.length);
        }
        return localPoints;
    }
    exports.lslice = lslice;
    function samplesamplerate(data) {
        // rediculous way to get sample rate:
        var r1 = Math.floor(Math.random() * (data.length - 5)) + 2;
        var rates = [data[r1 - 1].millis - data[r1 - 2].millis, data[r1].millis - data[r1 - 1].millis, data[r1 + 1].millis - data[r1].millis, data[r1 + 2].millis - data[r1 + 1].millis, data[r1 + 3].millis - data[r1 + 2].millis];
        var medianrate = median(rates); //ms
        return 1000 / medianrate; //Hz,samples/sec
    }
    exports.samplesamplerate = samplesamplerate;
    function filterlengthfromsamplerate(samplerate) {
        // magical adjustible acceleration filter , fl should be odd
        var fl = 7;
        if (samplerate >= 25) {
            fl = 25;
        }
        else if (samplerate >= 20) {
            fl = 21;
        }
        else if (samplerate >= 10) {
            fl = 7;
        }
        else {
            fl = 5;
        }
        return fl;
    }
    exports.filterlengthfromsamplerate = filterlengthfromsamplerate;
});
//# sourceMappingURL=/assets/javascripts/tracks/trackdata-processing.js.map;
